import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import StatusBadge from '@/components/StatusBadge';
import AccessCountdown from '@/components/AccessCountdown';
import {
  assignTenantReferrer,
  bulkAssignTenantReferrer,
  listManagers,
  listTenants,
  type AccessCounts,
  type AccessFilterKey,
  type ManagerListItem,
  type TenantListItem,
  type SubscriptionStatusKey,
} from '@/lib/operatorApi';
import { formatDate, formatKop, formatNumber } from '@/lib/format';

const FILTERS: { value: SubscriptionStatusKey | 'all'; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'active', label: 'Активные' },
  { value: 'trial', label: 'Trial' },
  { value: 'past_due', label: 'Просрочены' },
  { value: 'cancelled', label: 'Отменены' },
];

// Срок доступа — той же формулой, что и блокировка (считает бэкенд). Под
// фильтром строки идут по срочности: кому раньше платить — сверху.
const ACCESS_FILTERS: { value: AccessFilterKey; label: string; hint?: string }[] = [
  { value: 'all', label: 'Любой срок' },
  { value: 'expiring', label: 'Осталось ≤ 3 дн.', hint: 'Доступ заканчивается в ближайшие 3 дня' },
  { value: 'due', label: 'Подходит оплата', hint: 'Срок заканчивается в ближайшие 7 дней' },
  {
    value: 'grace',
    label: 'Просрочены, ещё работают',
    hint: 'Срок вышел, касса работает ещё до 3 дней, потом доступ закроется',
  },
  { value: 'blocked', label: 'Доступ закрыт', hint: 'Новые заказы и смены закрыты за неоплату' },
];

export default function Tenants() {
  const location = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<SubscriptionStatusKey | 'all'>('all');
  const [referrerFilter, setReferrerFilter] = useState('all');
  const [accessFilter, setAccessFilter] = useState<AccessFilterKey>('all');
  const [accessCounts, setAccessCounts] = useState<AccessCounts | null>(null);
  const [rows, setRows] = useState<TenantListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const [managers, setManagers] = useState<ManagerListItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const showFlash = useCallback((msg: string) => {
    setFlash(msg);
    window.setTimeout(() => setFlash(null), 3500);
  }, []);

  // Сообщение после редиректа (например, удаление арендатора). Показываем
  // один раз и сразу чистим location.state, чтобы не всплывало при возврате.
  useEffect(() => {
    const msg = (location.state as { flash?: string } | null)?.flash;
    if (!msg) return;
    setFlash(msg);
    navigate(location.pathname, { replace: true, state: null });
    const t = setTimeout(() => setFlash(null), 3500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Активные менеджеры — для селектов привязки. Список короткий, грузим один раз.
  useEffect(() => {
    listManagers({ status: 'active', limit: 200 })
      .then((r) => setManagers(r.items))
      .catch(() => setManagers([]));
  }, []);

  // Дебаунсим поиск, чтобы не бомбить бекенд каждой буквой.
  useEffect(() => {
    let alive = true;
    const t = setTimeout(() => {
      setLoading(true);
      setError(null);
      listTenants({
        q: query || undefined,
        status: filter,
        referrer: referrerFilter,
        access: accessFilter,
        limit: 200,
      })
        .then((r) => {
          if (!alive) return;
          setRows(r.results);
          setTotal(r.total);
          setAccessCounts(r.accessCounts ?? null);
          // Отметки живут только для видимых строк: после смены фильтра
          // «привязать выбранные» не должно задеть то, чего уже нет на экране.
          const visible = new Set(r.results.map((x) => x.id));
          setSelectedIds((prev) => new Set([...prev].filter((id) => visible.has(id))));
        })
        .catch((e: Error) => { if (alive) setError(e.message); })
        .finally(() => { if (alive) setLoading(false); });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [query, filter, referrerFilter, accessFilter]);

  const managerById = useMemo(
    () => new Map(managers.map((m) => [m.id, m])),
    [managers],
  );

  /** Привязка одной строки: оптимистично, с откатом при ошибке. */
  const assignOne = async (tenant: TenantListItem, managerId: string | null) => {
    const prev = tenant.referrer;
    const next = managerId
      ? (() => {
          const m = managerById.get(managerId);
          return m ? { id: m.id, name: m.name, code: m.code } : null;
        })()
      : null;

    setRows((cur) => cur.map((r) => (r.id === tenant.id ? { ...r, referrer: next } : r)));
    setBusyIds((cur) => new Set(cur).add(tenant.id));
    try {
      await assignTenantReferrer(tenant.id, managerId);
      showFlash(
        managerId
          ? `«${tenant.name}» → ${next?.name ?? 'менеджер'}`
          : `«${tenant.name}» отвязано от менеджера`,
      );
    } catch (e) {
      setRows((cur) => cur.map((r) => (r.id === tenant.id ? { ...r, referrer: prev } : r)));
      setError(e instanceof Error ? e.message : 'Не удалось изменить привязку');
    } finally {
      setBusyIds((cur) => {
        const s = new Set(cur);
        s.delete(tenant.id);
        return s;
      });
    }
  };

  const applyBulk = async (managerId: string | null) => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    const label = managerId ? managerById.get(managerId)?.name ?? 'менеджеру' : 'никому';
    if (!window.confirm(`Привязать ${ids.length} завед. к: ${label}?`)) return;
    setBulkBusy(true);
    setError(null);
    try {
      const res = await bulkAssignTenantReferrer(ids, managerId);
      const m = managerId ? managerById.get(managerId) ?? null : null;
      const next = m ? { id: m.id, name: m.name, code: m.code } : null;
      setRows((cur) => cur.map((r) => (selectedIds.has(r.id) ? { ...r, referrer: next } : r)));
      setSelectedIds(new Set());
      showFlash(
        `Изменено: ${res.changed}${res.skipped ? `, без изменений: ${res.skipped}` : ''}`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось привязать');
    } finally {
      setBulkBusy(false);
    }
  };

  const toggleRow = (id: string) =>
    setSelectedIds((cur) => {
      const s = new Set(cur);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });

  const allVisibleSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id));
  const toggleAll = () =>
    setSelectedIds(allVisibleSelected ? new Set() : new Set(rows.map((r) => r.id)));

  const withoutManager = rows.filter((r) => !r.referrer).length;
  const anyFilter = !!query || filter !== 'all' || referrerFilter !== 'all' || accessFilter !== 'all';

  return (
    <div>
      <PageHeader
        eyebrow="Арендаторы"
        title="Заведения на платформе"
        description={`Всего: ${total}. Подписки, продление trial, блокировка и привязка к менеджеру.`}
      />

      <div className="page-pad">
        {flash && (
          <div className="mb-4 border border-green-600/30 bg-green-50 px-4 py-3 text-[13px] text-green-700">
            {flash}
          </div>
        )}

        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-1">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setFilter(f.value)}
                className={
                  'border px-3 py-1.5 text-[12px] font-medium transition ' +
                  (filter === f.value
                    ? 'border-ink bg-ink text-white'
                    : 'border-line bg-white text-ink/70 hover:border-ink/50')
                }
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row md:items-center">
            <select
              className="input sm:w-56"
              value={referrerFilter}
              onChange={(e) => setReferrerFilter(e.target.value)}
              aria-label="Фильтр по менеджеру"
            >
              <option value="all">Менеджер: любой</option>
              <option value="none">Без менеджера</option>
              <option value="any">С менеджером</option>
              {managers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.code})
                </option>
              ))}
            </select>
            <div className="relative md:w-80">
              <Search
                size={14}
                strokeWidth={1.75}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink/40"
              />
              <input
                type="search"
                className="input pl-9"
                placeholder="Поиск по названию, email, телефону…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-1">
          <span className="mr-2 text-[11px] uppercase tracking-[0.12em] text-ink/45">Срок</span>
          {ACCESS_FILTERS.map((f) => {
            const count = f.value === 'all' ? null : accessCounts?.[f.value] ?? null;
            return (
              <button
                key={f.value}
                type="button"
                title={f.hint}
                onClick={() => setAccessFilter(f.value)}
                className={
                  'border px-3 py-1.5 text-[12px] font-medium transition ' +
                  (accessFilter === f.value
                    ? 'border-ink bg-ink text-white'
                    : 'border-line bg-white text-ink/70 hover:border-ink/50')
                }
              >
                {f.label}
                {count !== null && (
                  <span className="ml-1.5 font-mono text-[11px] tabular-nums opacity-60">{count}</span>
                )}
              </button>
            );
          })}
        </div>

        {error && (
          <div className="mb-4 border border-red-600/30 bg-red-50 px-4 py-3 text-[13px] text-red-700">
            {error}
          </div>
        )}

        {referrerFilter === 'none' && !loading && rows.length > 0 && (
          <div className="mb-4 border border-line bg-paper px-4 py-2.5 text-[12px] text-ink/60">
            {withoutManager} завед. без менеджера — отметьте нужные и привяжите одним действием.
          </div>
        )}

        {/* Desktop: таблица */}
        <div className="card hidden overflow-hidden md:block">
          <table className="table-grid">
            <thead>
              <tr>
                <th className="w-10">
                  <input
                    type="checkbox"
                    className="align-middle accent-ink"
                    checked={allVisibleSelected}
                    onChange={toggleAll}
                    aria-label="Выбрать все"
                  />
                </th>
                <th>Заведение</th>
                <th>Владелец</th>
                <th>Менеджер</th>
                <th>Статус</th>
                <th className="text-right">Осталось дней</th>
                <th className="text-right">Оборот за 30 д</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} className={selectedIds.has(t.id) ? 'bg-paper' : undefined}>
                  <td>
                    <input
                      type="checkbox"
                      className="align-middle accent-ink"
                      checked={selectedIds.has(t.id)}
                      onChange={() => toggleRow(t.id)}
                      aria-label={`Выбрать ${t.name}`}
                    />
                  </td>
                  <td>
                    <Link to={`/tenants/${t.id}`} className="font-medium hover:underline">
                      {t.name}
                    </Link>
                    {t.networkSize > 1 && <NetworkTag size={t.networkSize} />}
                    <div className="text-[11px] text-ink/50">
                      {t.city ? `${t.city} · ` : ''}создано {formatDate(t.createdAt)}
                    </div>
                  </td>
                  <td>
                    <div className="text-ink/90">{t.ownerName ?? '—'}</div>
                    <div className="text-[11px] text-ink/50">{t.ownerEmail ?? '—'}</div>
                  </td>
                  <td>
                    <ManagerPicker
                      value={t.referrer?.id ?? ''}
                      managers={managers}
                      busy={busyIds.has(t.id)}
                      onChange={(id) => void assignOne(t, id)}
                    />
                  </td>
                  <td>
                    <StatusBadge status={t.status} />
                  </td>
                  <td className="text-right">
                    {t.accessEndsAt ? (
                      <AccessCountdown
                        endsAt={t.accessEndsAt}
                        subject={t.status === 'trial' ? 'trial' : 'paid'}
                        startedAt={t.createdAt}
                        accessState={t.accessState}
                        graceDaysLeft={t.graceDaysLeft}
                      />
                    ) : (
                      <span className="text-[12px] text-ink/40">—</span>
                    )}
                    {t.autoRenew && <AutoRenewTag />}
                  </td>
                  <td className="text-right font-mono text-[12px] text-ink/80">
                    {formatKop(t.revenueLast30d)}
                    <div className="text-[11px] font-sans text-ink/50">
                      {formatNumber(t.ordersLast30d)} заказов
                    </div>
                  </td>
                  <td className="text-right">
                    <Link
                      to={`/tenants/${t.id}`}
                      className="text-[12px] text-ink/60 hover:text-ink"
                    >
                      Открыть →
                    </Link>
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-10 text-center text-ink/50">
                    {anyFilter ? 'Ничего не найдено по фильтру' : 'Арендаторов пока нет'}
                  </td>
                </tr>
              )}
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-10 text-center text-ink/40">
                    Загрузка…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile: карточки */}
        <ul className="card divide-y divide-line md:hidden">
          {rows.map((t) => (
            <li key={t.id} className="px-4 py-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1 accent-ink"
                  checked={selectedIds.has(t.id)}
                  onChange={() => toggleRow(t.id)}
                  aria-label={`Выбрать ${t.name}`}
                />
                <Link to={`/tenants/${t.id}`} className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[15px] font-medium text-ink">{t.name}</div>
                      {t.networkSize > 1 && <NetworkTag size={t.networkSize} />}
                      <div className="mt-0.5 truncate text-[12px] text-ink/60">
                        {t.ownerEmail ?? t.ownerName ?? '—'}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <StatusBadge status={t.status} />
                      <AccessCountdown
                        endsAt={t.accessEndsAt}
                        subject={t.status === 'trial' ? 'trial' : 'paid'}
                        startedAt={t.createdAt}
                        accessState={t.accessState}
                        graceDaysLeft={t.graceDaysLeft}
                      />
                      {t.autoRenew && <AutoRenewTag />}
                    </div>
                  </div>

                  <dl className="mt-3 grid grid-cols-2 gap-y-1.5 text-[12px]">
                    <dt className="text-ink/50">Город</dt>
                    <dd className="text-right text-ink/80">{t.city ?? '—'}</dd>
                    <dt className="text-ink/50">Оборот 30д</dt>
                    <dd className="text-right font-mono text-ink/80">
                      {formatKop(t.revenueLast30d)}
                    </dd>
                    <dt className="text-ink/50">След. списание</dt>
                    <dd className="text-right font-mono text-ink/80">
                      {t.nextBillingAt ? formatDate(t.nextBillingAt) : '—'}
                    </dd>
                  </dl>
                </Link>
              </div>
              <div className="mt-3 pl-7">
                <div className="mb-1 text-[11px] uppercase tracking-[0.12em] text-ink/45">
                  Менеджер
                </div>
                <ManagerPicker
                  value={t.referrer?.id ?? ''}
                  managers={managers}
                  busy={busyIds.has(t.id)}
                  onChange={(id) => void assignOne(t, id)}
                />
              </div>
            </li>
          ))}
          {!loading && rows.length === 0 && (
            <li className="px-4 py-10 text-center text-[13px] text-ink/50">
              {anyFilter ? 'Ничего не найдено по фильтру' : 'Арендаторов пока нет'}
            </li>
          )}
          {loading && rows.length === 0 && (
            <li className="px-4 py-10 text-center text-[13px] text-ink/40">Загрузка…</li>
          )}
        </ul>
      </div>

      {selectedIds.size > 0 && (
        <BulkBar
          count={selectedIds.size}
          managers={managers}
          busy={bulkBusy}
          onApply={applyBulk}
          onClear={() => setSelectedIds(new Set())}
        />
      )}
    </div>
  );
}

/** Подписка продлится сама: карта ЮKassa сохранена. */
function AutoRenewTag() {
  return (
    <div className="mt-0.5 text-[11px] text-ink/45" title="Карта ЮKassa сохранена — продлится автосписанием">
      автоплатёж
    </div>
  );
}

/** Селект привязки к менеджеру — одинаковый в таблице и в мобильной карточке. */
function ManagerPicker({
  value,
  managers,
  busy,
  onChange,
}: {
  value: string;
  managers: ManagerListItem[];
  busy: boolean;
  onChange: (managerId: string | null) => void;
}) {
  return (
    <select
      className={
        'w-full min-w-[150px] border bg-white px-2 py-1.5 text-[12px] transition focus:border-ink focus:outline-none ' +
        (value ? 'border-line text-ink' : 'border-line text-ink/45')
      }
      value={value}
      disabled={busy}
      onChange={(e) => onChange(e.target.value || null)}
      onClick={(e) => e.stopPropagation()}
    >
      <option value="">— не привязан —</option>
      {managers.map((m) => (
        <option key={m.id} value={m.id}>
          {m.name} ({m.code})
        </option>
      ))}
    </select>
  );
}

/** Панель массового действия — появляется, когда отмечена хотя бы одна строка. */
function BulkBar({
  count,
  managers,
  busy,
  onApply,
  onClear,
}: {
  count: number;
  managers: ManagerListItem[];
  busy: boolean;
  onApply: (managerId: string | null) => void;
  onClear: () => void;
}) {
  const [target, setTarget] = useState('');

  return (
    <div className="sticky bottom-0 z-20 border-t border-line bg-white/95 px-4 py-3 backdrop-blur md:px-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-[13px] text-ink">
          Выбрано: <span className="font-medium">{count}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="input sm:w-60"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            aria-label="Менеджер для привязки"
          >
            <option value="">Выберите менеджера…</option>
            {managers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} ({m.code})
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn-dark"
            disabled={busy || !target}
            onClick={() => onApply(target)}
          >
            {busy ? 'Привязываем…' : 'Привязать'}
          </button>
          <button
            type="button"
            className="btn-outline"
            disabled={busy}
            onClick={() => onApply(null)}
          >
            Отвязать
          </button>
          <button type="button" className="btn-ghost" onClick={onClear} aria-label="Снять выбор">
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Метка точки сети: у владельца несколько заведений. Подписка, статус и срок в
 * строке — собственные, каждая точка оплачивается отдельно; метка лишь
 * подсказывает, что соседние строки с тем же владельцем — одна сеть.
 */
function NetworkTag({ size }: { size: number }) {
  return (
    <span
      className="mt-1 inline-flex items-center gap-1 border border-line bg-paper px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-ink/60"
      title="У владельца несколько заведений. Подписка у каждого своя, оплачиваются отдельно."
    >
      Сеть · {size} {pluralPoints(size)}
    </span>
  );
}

function pluralPoints(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'точка';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'точки';
  return 'точек';
}
